import React , {useMemo} from 'react';
import { TreeItem } from '@mui/x-tree-view';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import { greenCheckIcon, redCrossIcon } from '../assets';
import { ITestCase } from '../types/interfaces/testCase.interface';
import { buildNodeKey } from '../utils/nodeId';
import { useNavigate, useParams } from 'react-router-dom';
import TreeItemLabel from './TreeItemLabel';
import { formatTimestamp } from '../helpers/date.helper';
import {Modal} from '../ui/modal';
import { uniqueId } from '../helpers/form.helper';
import { appConfigSelector } from '../store/selectors/app.selector';
import { CONFIGNAME } from '../types/global.enum';
import { useDispatch, useSelector } from 'react-redux';
import {
  isTestCaseRunCleared,
  isTestCaseRunning,
} from '../store/selectors/run.selector';
import { setTestCaseRunCleared } from '../store/resuders/run.reducer';

interface TestCaseTreeItemProps {
  testCase: ITestCase;
  parentProjectId: number;
  onContextMenu: (
    event: React.MouseEvent,
    nodeId: string,
    nodeType: 'testcase',
    parentId: number
  ) => void;
  onSelectedItemsChange: (event: React.SyntheticEvent | null, itemIds: string[] | string) => void;
  showModifiedDates: boolean;
  filterText: string;
}

export const TestCaseTreeItem: React.FC<TestCaseTreeItemProps> = ({
  testCase,
  parentProjectId,
  onContextMenu,
  onSelectedItemsChange,
  showModifiedDates,
  filterText,
}) => {
  const navigate = useNavigate();
  const { projectId: currentProjectId, testCaseId: currentTestCaseId } = useParams<{ projectId?: string; testCaseId?: string }>();
  const dispatch = useDispatch();
  const testCaseRunning = useSelector(
    isTestCaseRunning(Number(currentProjectId), Number(currentTestCaseId))
  );
  const testCaseRunCleared = useSelector(
    isTestCaseRunCleared(Number(currentProjectId), Number(currentTestCaseId))
  );
 
 
  const [showModal, setShowModal] = React.useState('');
  const [showRunModal, setShowRunModal] = React.useState('');
  const [pendingPath, setPendingPath] = React.useState<string | null>(null);
  const appConfig = useSelector(appConfigSelector);
  const autoSaveEnabled = useMemo(()=>appConfig?.find(c=>c.configName == CONFIGNAME.AUTOSAVE && c.configValue === 'true') ,[appConfig])
      console.log(autoSaveEnabled);
  // const autoSaveEnabled =
  //   useAppSelector(getConfigValueByName(CONFIGNAME.AUTOSAVE)) === 'true';

    console.log(autoSaveEnabled);
const invalidTestCaseId = typeof testCase.testCaseId !== 'number';
  const lastRun = (testCase as any)?.lastTestRun;
  const lastRunRunning =
    lastRun === 'RUNNING' ||
    (Array.isArray(lastRun) && lastRun[0]?.status === 'RUNNING');
  React.useEffect(() => {
    if (
      invalidTestCaseId ||
      parentProjectId !== Number(currentProjectId) ||
      testCase.testCaseId !== Number(currentTestCaseId) ||
      lastRunRunning ||
      testCaseRunCleared
    ) {
      return;
    }
    dispatch(
      setTestCaseRunCleared({
        projectId: parentProjectId,
        testCaseId: testCase.testCaseId,
      })
    );
  }, [
    dispatch,
    lastRunRunning,
    testCaseRunCleared,
    parentProjectId,
    testCase.testCaseId,
    currentProjectId,
    currentTestCaseId,
    invalidTestCaseId,
  ]);

  if (invalidTestCaseId) {
    console.warn('Skipping test case with missing/invalid ID:', testCase);
    return null;
  }

  const itemId = buildNodeKey({ type: 'testcase', id: testCase.testCaseId });
  const label = `${testCase.testCaseId} - ${testCase.name || 'Test Case'}`;
  const subText =
    showModifiedDates && testCase.lastModifiedTime
      ? formatTimestamp(testCase.lastModifiedTime)
      : undefined;

  // Determine last run status icon
  let statusIcon: React.ReactNode = null;
  if (lastRun) {
    if (lastRunRunning && !testCaseRunCleared) {
      statusIcon = <PendingOutlinedIcon style={{ fill: 'blue', fontSize: 16 }} />;
    } else if (!lastRunRunning) {
      const pass =
        lastRun === 'PASS' ||
        (Array.isArray(lastRun) && lastRun[0]?.status === 'PASS');
      statusIcon = (
        <img
          src={pass ? greenCheckIcon : redCrossIcon}
          alt=""
          style={{ width: 16, height: 16 }}
        />
      );
    }
  }

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onSelectedItemsChange(event, itemId);

    //navigate(`/folders/project/${parentProjectId}/testcase/${testCase.testCaseId}`);
    const targetPath = `/folders/project/${parentProjectId}/testcase/${testCase.testCaseId}`;

    if (
      testCaseRunning &&
      !testCaseRunCleared &&
      (Number(currentProjectId) !== parentProjectId ||
        Number(currentTestCaseId) !== testCase.testCaseId)
    ) {
      setPendingPath(targetPath);
      setShowRunModal(uniqueId());
      return;
    }

    proceedToNavigation(targetPath);
  };
  
  const proceedToNavigation = (targetPath: string) => {
    const hasUnsaved = localStorage.getItem('showEditorwarning') === 'true';
    if (hasUnsaved === true) {
      if (autoSaveEnabled) {
        window.dispatchEvent(
          new CustomEvent('global-autosave', { detail: { nextPath: targetPath } })
        );
      } else {
        setPendingPath(targetPath); // store the path to navigate after confirmation
        setShowModal(uniqueId());
      }
    } else {
      navigate(targetPath);
    }
  };

  const handleConfirmNavigation = () => {
    if (pendingPath) {
      localStorage.setItem('showEditorwarning', 'false');
      navigate(pendingPath);
    }
    setShowModal('');
    setPendingPath(null);
  };

  const handleCancelNavigation = () => {
    setShowModal('');
    setPendingPath(null);
  };

  const handleRunConfirm = () => {
    if (pendingPath) {
      const path = pendingPath;
      setShowRunModal('');
      setPendingPath(null);
      proceedToNavigation(path);
    }
  };

  const handleRunCancel = () => {
    setShowRunModal('');
    setPendingPath(null);
  };


  return (
    <>
    <TreeItem
      key={itemId}
      itemId={itemId}
      slots={{ icon: ChevronRightIcon }}
      label={
        <TreeItemLabel
          text={label}
          statusIcon={statusIcon}
          subText={subText}
          searchTerm={filterText}
        />
      }
      slotProps={{
        icon: { fontSize: 'medium', color: 'action' },
      }}
      onContextMenu={(e) => onContextMenu(e, itemId, 'testcase', parentProjectId)}
      onClick={handleClick}
    />
     <Modal
      isOpen={showRunModal}
      title="Test Case Running"
      small={true}
      body={
        <div>
          <p>The current test case is running. Do you want to switch to another test case?</p>
        </div>
      }
      ButtonDelete={handleRunConfirm}
      deleteConfirmation={true}
      onClosed={handleRunCancel}
    />
    <Modal
      isOpen={showModal}
      title="Unsved Changes"
      small={true}               
      body={
          <div>
              <p>You have unsaved changes. Are you sure you want to close the editor?</p>
              
          </div>
      }
      ButtonDelete={handleConfirmNavigation}
      deleteConfirmation={true}
    />
    
  </>
  );
}; 